import type { Assistant } from "@inrev/assistant";
import type { AssistantConversationId } from "@inrev/common";
import { Option } from "@inrev/core";
import { cn } from "@portal/components/lib/utils";
import { List, Text } from "@portal/components/primitives";
import { trackingAttrs } from "@portal/components/utils";
import type { DateTime } from "luxon";
import type { Ref } from "react";
import {
	assistantConversationTime,
	assistantNewChatLabel,
} from "../../admin/shared/tools/assistant/assistantCopy";

const variantClasses = {
	compact: {
		row: "py-[9px]",
		idle: "border-transparent hover:bg-surface-light",
		selected: "border-bc-default bg-surface-light",
		title: "text-[14px] leading-[18px]",
		time: "text-[12px]",
		preview: "text-[13px] leading-[18px]",
	},
	rail: {
		row: "py-2",
		idle: "border-transparent hover:bg-surface-dark",
		selected: "border-bc-default bg-surface-base",
		title: "text-[13px] leading-[17px]",
		time: "text-[11px]",
		preview: "text-[12px] leading-[16px]",
	},
};

export type VerniHistoryVariant = keyof typeof variantClasses;

type VerniHistoryRowProps = {
	conversation: Assistant.Dtos.Conversation.List.Item;
	variant: VerniHistoryVariant;
	selected: boolean;
	now: DateTime;
	onSelect: (conversationId: AssistantConversationId) => void;
	ref?: Ref<HTMLButtonElement>;
};

export const VerniHistoryRow = ({
	conversation,
	variant,
	selected,
	now,
	onSelect,
	ref,
}: VerniHistoryRowProps) => {
	const classes = variantClasses[variant];
	const title = Option.getOrElse(
		Option.first([conversation.title, conversation.fallbackTitle]),
		assistantNewChatLabel,
	);

	return (
		<List.Item>
			<button
				aria-current={selected ? "true" : undefined}
				className={cn(
					"flex w-full min-w-0 cursor-pointer flex-col gap-[3px] rounded-md border px-2.5 text-left transition-colors",
					"focus-visible:outline-none focus-visible:ring focus-visible:ring-brand-accent focus-visible:ring-opacity-30 focus-visible:ring-offset-0",
					classes.row,
					selected ? classes.selected : classes.idle,
				)}
				data-testid="verni-history-row"
				onClick={() => onSelect(conversation.id)}
				ref={ref}
				type="button"
				{...trackingAttrs("verni-history-row")}
			>
				<span className="flex w-full items-baseline gap-2">
					<Text
						className={cn("min-w-0 flex-1 font-medium", classes.title)}
						color="primary"
						data-testid="verni-history-row-title"
						truncate
						type="body"
					>
						{title}
					</Text>
					<Text
						className={cn("shrink-0 font-normal", classes.time)}
						color="tertiary"
						type="caption"
					>
						<time dateTime={conversation.lastActivityAt}>
							{assistantConversationTime(conversation.lastActivityAt, now)}
						</time>
					</Text>
				</span>
				{Option.isSome(conversation.preview) && (
					<Text
						className={cn("block w-full", classes.preview)}
						color="tertiary"
						data-testid="verni-history-row-preview"
						truncate
						type="body"
					>
						{conversation.preview}
					</Text>
				)}
			</button>
		</List.Item>
	);
};
